import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LiveStepDisplay } from "./live-walkthrough-screenshot";
import type { UnifiedLiveFinding } from "./types";

interface LiveTimeScrubberProps {
  steps: LiveStepDisplay[];
  activeStepIdx: number;
  onStepChange: (idx: number) => void;
  isLive: boolean;
  stepsBehindLive: number;
  onResumeLive: () => void;
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
  findingsByStep?: Map<number, UnifiedLiveFinding[]>;
}

export function LiveTimeScrubber({
  steps,
  activeStepIdx,
  onStepChange,
  isLive,
  stepsBehindLive,
  onResumeLive,
  isRunning,
  isComplete,
  isErrored,
  findingsByStep,
}: LiveTimeScrubberProps) {
  if (steps.length === 0) return null;

  const displayedStep = steps[activeStepIdx];
  const progressPercent =
    steps.length > 1 ? (activeStepIdx / (steps.length - 1)) * 100 : 100;

  return (
    <div
      aria-label="Time scrubber"
      className="border-t border-border/60 bg-background/95 p-3 backdrop-blur-sm"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onStepChange(Math.max(0, activeStepIdx - 1))}
              disabled={activeStepIdx === 0}
              aria-label="Previous step"
              className="flex size-7 items-center justify-center rounded-md border border-border/80 bg-background text-foreground transition-colors hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() =>
                onStepChange(Math.min(steps.length - 1, activeStepIdx + 1))
              }
              disabled={activeStepIdx === steps.length - 1}
              aria-label="Next step"
              className="flex size-7 items-center justify-center rounded-md border border-border/80 bg-background text-foreground transition-colors hover:bg-muted disabled:opacity-40"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

          <span className="text-xs font-medium tabular-nums text-foreground">
            Step {displayedStep?.stepNumber ?? activeStepIdx + 1}
            <span className="text-muted-foreground"> / {steps.length}</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isRunning && !isComplete && !isErrored && (
            <>
              {isLive ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-surface-tonal px-2.5 py-0.5 text-xs font-semibold text-foreground">
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full bg-mark-helps animate-pulse"
                  />
                  LIVE
                </span>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={onResumeLive}
                  className="gap-1.5 bg-foreground text-background font-semibold shadow-xs hover:bg-foreground/90"
                  aria-label="Go to live step"
                >
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full bg-mark-helps animate-pulse"
                  />
                  Go to Live ({stepsBehindLive} behind)
                </Button>
              )}
            </>
          )}

          {isComplete && (
            <span className="text-xs font-medium text-muted-foreground">
              Completed
            </span>
          )}
        </div>
      </div>

      {steps.length > 1 && (
        <div className="relative flex h-4 w-full items-center">
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-foreground transition-all duration-75"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {steps.length <= 40 && (
            <div className="pointer-events-none absolute inset-x-0 flex justify-between px-0.5">
              {steps.map((step) => {
                const stepFindings = findingsByStep?.get(step.stepNumber) ?? [];
                const hasHindrance = stepFindings.some(
                  (f) => f.type !== "helps",
                );
                const hasHelps = stepFindings.some((f) => f.type === "helps");
                return (
                  <span
                    key={step.id}
                    className={cn(
                      "size-1 rounded-full",
                      hasHindrance
                        ? "bg-mark-hinders"
                        : hasHelps
                          ? "bg-mark-helps"
                          : "bg-transparent",
                    )}
                  />
                );
              })}
            </div>
          )}

          <div
            className="pointer-events-none absolute size-3.5 -translate-x-1/2 rounded-full border-2 border-background bg-foreground shadow-xs"
            style={{ left: `${progressPercent}%` }}
          />

          <input
            type="range"
            min={0}
            max={steps.length - 1}
            value={activeStepIdx}
            onChange={(e) => onStepChange(Number(e.target.value))}
            aria-label="Scrub walkthrough step"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
      )}
    </div>
  );
}
